import { useCallback, useMemo, useState } from 'react';

import { ClearSelectionEvent, SelectionWithSearchProps } from './select.types';
import { MenuItem } from '../filter-combo-box/filter-combo-box.types';

function hasValue(value: string | undefined): boolean {
  return value !== undefined;
}

export const useSelectionWithSearch = <T extends MenuItem>(props: SelectionWithSearchProps<T>) => {
  const { data, value, defaultValue, defaultSearchTerm, onChange, onSearchCallback, setMenuOpen, onClear } = props;
  const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm ?? '');

  const [innerValue, setInnerValue] = useState<string | undefined>(() => {
    // anything other than undefined is considered a valid value, including null, empty string, etc.
    //   This makes sure that defaultValue is used if value is not provided
    //   Please make sure not change it to satisfy edge cases
    if (hasValue(value)) {
      return value;
    } else if (hasValue(defaultValue)) {
      return defaultValue;
    }
  });

  // This makes component behavior controlled if value is provided
  //  Otherwise, it is uncontrolled and uses internal state
  //  Please make sure not change it to satisfy edge cases
  const mergedValue = value === undefined ? innerValue : value;

  const selectedItem = useMemo(() => {
    return data.find((item) => item.value === mergedValue);
  }, [data, mergedValue]);

  const onSearch = useCallback(
    (searchString: string) => {
      setSearchTerm(searchString);
      onSearchCallback(searchString);
    },
    [onSearchCallback]
  );

  const onToggleItem = useCallback(
    (toggledItem: T, isSelected: boolean) => {
      const newSelection = isSelected ? toggledItem : undefined;
      setInnerValue(newSelection?.value);
      onChange(newSelection, data);
      if (isSelected) {
        // after an item has been selected, close the pop selection menu
        setMenuOpen(false);
        onSearch('');
      }
    },
    [onChange, data, setMenuOpen, onSearch]
  );

  const onClearSelection = useCallback(
    (e: ClearSelectionEvent) => {
      if (selectedItem) {
        onToggleItem(selectedItem, false);
      }
      onSearch('');
      e?.stopPropagation();
      onClear?.(e);
    },
    [selectedItem, onSearch, onToggleItem, onClear]
  );

  return { searchTerm, selectedItem, onToggleItem, onClearSelection, onSearch };
};
